import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { ReportSummary, Story, StoryEvent } from '../api/types';
import { absTime, stanceGlyph, when } from '../hooks/format';

/** Chapters = events grouped by correlation id, newest first (docs/design/story-visualisation.md). */
interface Chapter {
  id: string;
  at: string;
  kind: string;
  tone: 'ai' | 'user' | 'broker' | 'alert' | 'system';
  heading: string;
  events: StoryEvent[];
  reportId: string | null;
}

function asText(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v);
}

const ORDER_TERMINAL = new Set(['FILLED', 'CANCELLED', 'REJECTED', 'EXPIRED', 'FAILED_PRE_SUBMIT']);

export function buildChapters(story: Story): Chapter[] {
  const groups = new Map<string, StoryEvent[]>();
  for (const e of story.events) groups.set(e.correlation_id, [...(groups.get(e.correlation_id) ?? []), e]);
  const chapters: Chapter[] = [];
  for (const [id, evs] of groups) {
    evs.sort((a, b) => a.seq - b.seq);
    const first = evs[0]!;
    const types = new Set(evs.map((e) => e.type));
    const reportId = (evs.find((e) => typeof e.payload['report_id'] === 'string')?.payload['report_id'] as string | undefined) ?? null;
    let heading = first.type;
    let kind = 'System';
    let tone: Chapter['tone'] = 'system';
    if (types.has('checkpoint.run_started')) {
      kind = 'Checkpoint';
      tone = 'ai';
      const purpose = story.events.find((e) => e.type === 'checkpoint.scheduled' && e.payload['checkpoint_id'] === first.payload['checkpoint_id'])?.payload['purpose'] as string | undefined;
      heading = purpose ?? 'Checkpoint';
    } else if (types.has('narrative.seeded')) {
      kind = 'Seeded';
      tone = 'user';
      heading = 'Narrative seeded';
    } else if (types.has('narrative.developed')) {
      kind = 'Developed';
      tone = 'ai';
      heading = 'Thesis v1 developed';
    } else if (types.has('narrative.activated')) {
      kind = 'Started';
      tone = 'user';
      const p = evs.find((e) => e.type === 'narrative.activated')!.payload as { mode: string; allocation: { amount: string; currency: string } };
      heading = `Started in ${p.mode} with ${p.allocation.currency} ${Number(p.allocation.amount).toLocaleString()}`;
    } else if (types.has('kill_switch.engaged')) {
      kind = 'Kill switch';
      tone = 'alert';
      heading = `Kill switch engaged: ${asText(evs.find((e) => e.type === 'kill_switch.engaged')!.payload['reason'])}`;
    } else if (types.has('fill.recorded') || types.has('order.status_changed') || types.has('order.created')) {
      kind = 'Execution';
      tone = 'broker';
      heading = summariseExecution(evs);
    } else if (types.has('intent.risk_rejected') || types.has('intent.held_for_user') || types.has('intent.risk_approved') || types.has('intent.observed_only')) {
      kind = 'Risk';
      tone = 'system';
      heading = summariseRisk(evs);
    } else if (types.has('trigger.fired')) {
      kind = 'Trigger';
      tone = 'alert';
      heading = String((evs.find((e) => e.type === 'checkpoint.triggered')?.payload['reason'] as string) ?? 'Trigger fired');
    } else if (first.actor.startsWith('USER')) {
      kind = 'Owner';
      tone = 'user';
      heading = first.type.replace('narrative.', '').replace(/_/g, ' ');
    } else if (types.has('reconciliation.discrepancy')) {
      kind = 'Reconciliation';
      tone = 'alert';
      heading = 'Reconciliation discrepancy';
    } else if (types.has('evidence.stored') && evs.length === 1) {
      kind = 'Evidence';
      heading = `${String(first.payload['count'])} item(s) from ${String(first.payload['provider'])}`;
    }
    chapters.push({ id, at: first.occurred_at, kind, tone, heading, events: evs, reportId });
  }
  chapters.sort((a, b) => +new Date(b.at) - +new Date(a.at));
  return chapters;
}

function summariseExecution(evs: StoryEvent[]): string {
  const fills = evs.filter((e) => e.type === 'fill.recorded');
  if (fills.length) {
    const f = fills[fills.length - 1]!.payload as { qty: string; price: string; currency: string };
    return `Filled ${f.qty} @ ${f.price} ${f.currency}`;
  }
  const created = evs.find((e) => e.type === 'order.created');
  if (created) {
    const p = created.payload as { side: string; qty: string; limit_price: string | null };
    return `Order ${p.side} ${p.qty} @ ${p.limit_price ?? 'market'}`;
  }
  const status = evs.filter((e) => e.type === 'order.status_changed').at(-1);
  if (status) {
    const p = status.payload as { to: string; reason?: string };
    return `Order ${p.to.toLowerCase()}${p.reason ? `: ${p.reason}` : ''}`;
  }
  return 'Execution';
}

function summariseRisk(evs: StoryEvent[]): string {
  const rej = evs.find((e) => e.type === 'intent.risk_rejected');
  if (rej) return `Rejected: ${String(rej.payload['reason'])}`;
  if (evs.some((e) => e.type === 'intent.held_for_user')) return 'Held for your approval';
  if (evs.some((e) => e.type === 'intent.observed_only')) return `Observed only: ${String(evs.find((e) => e.type === 'intent.observed_only')!.payload['reason'])}`;
  const ok = evs.find((e) => e.type === 'intent.risk_approved');
  if (ok) return ok.payload['order_id'] ? 'Approved and ordered' : 'Approved';
  return 'Risk decision';
}

export function StoryView({ story, slug, baseCurrency }: { story: Story; slug: string; baseCurrency: string }) {
  const chapters = useMemo(() => buildChapters(story), [story]);
  if (chapters.length === 0) return <div className="empty">No events yet.</div>;
  return (
    <div className="page">
      {chapters.map((c) => (
        <ChapterCard key={c.id} chapter={c} report={c.reportId ? story.reports[c.reportId] : undefined} slug={slug} baseCurrency={baseCurrency} />
      ))}
    </div>
  );
}

function ChapterCard({ chapter, report, slug, baseCurrency }: { chapter: Chapter; report: ReportSummary | undefined; slug: string; baseCurrency: string }) {
  const [open, setOpen] = useState(false);
  const evs = chapter.events;
  const byType = (t: string) => evs.filter((e) => e.type === t);
  const collected = byType('checkpoint.evidence_collected')[0]?.payload as { evidence_count?: number; new_count?: number; gaps?: string[] } | undefined;
  const intents = byType('intent.proposed');
  const rejectedByContract = byType('intent.rejected_by_contract');
  const orders = byType('order.created');
  const fills = byType('fill.recorded');
  const statusChanges = byType('order.status_changed');
  const lastStatus = statusChanges.at(-1)?.payload as { to: string; reason?: string } | undefined;
  const scheduled = byType('checkpoint.scheduled');
  const rescheduled = byType('checkpoint.rescheduled');
  const cancelled = byType('checkpoint.cancelled');
  const triggers = byType('trigger.created');
  const thesis = byType('narrative.thesis_updated')[0]?.payload as { version: number; stance: string } | undefined;
  const ledger = evs.filter((e) => e.type.startsWith('ledger.'));
  const ai = byType('ai.call_completed');
  const failed = byType('checkpoint.failed')[0]?.payload as { error: string; retry_at: string | null } | undefined;
  return (
    <div className={`chapter ${chapter.tone}`}>
      <div className="row">
        <span className="t-cap">{chapter.kind} · {when(chapter.at)}</span>
        <button className="faint" style={{ background: 'none', border: 0, fontSize: 12 }} onClick={() => setOpen(!open)}>{open ? 'less' : 'more'}</button>
      </div>
      <div className="t-em" style={{ marginTop: 2 }}>{chapter.heading}</div>
      {thesis && <div className="section"><span className="pill">Thesis v{thesis.version} · {thesis.stance === 'INITIAL' ? '' : `${stanceGlyph(thesis.stance)} `}{thesis.stance}</span></div>}
      {report?.kind === 'CHECKPOINT' && (
        <div className="section">
          <div className="stance">{stanceGlyph(report.stance)} {report.stance}{report.confidence ? <span className="faint t-sec"> · confidence {(report.confidence.value * 100).toFixed(0)}%</span> : null}</div>
          <p style={{ margin: '6px 0 0' }}>{report.conclusion}</p>
          {report.what_changed && report.what_changed.length > 0 && (
            <div className="section"><div className="t-cap">What changed</div>{report.what_changed.map((w, i) => <div key={i} className="t-sec" style={{ fontWeight: w.significance === 'HIGH' ? 600 : 400 }}>• {w.summary}</div>)}</div>
          )}
          {report.no_action_rationale && <div className="t-sec muted" style={{ marginTop: 6 }}>No action: {report.no_action_rationale}</div>}
        </div>
      )}
      {report?.kind === 'DEVELOPMENT' && (
        <div className="section">
          <p style={{ margin: 0 }}>{report.owner_summary}</p>
          {report.corrections && report.corrections.length > 0 && <div className="section"><div className="t-cap">Corrections to the seed</div>{report.corrections.map((c, i) => <div key={i} className="t-sec">• {c}</div>)}</div>}
        </div>
      )}
      {failed && <div className="section t-sec warn">Failed: {failed.error}{failed.retry_at ? ` · retry ${when(failed.retry_at)}` : ''}</div>}
      {collected && (
        <div className="section t-sec muted">
          Knew: {collected.evidence_count ?? 0} evidence items{collected.new_count ? ` (${collected.new_count} new)` : ''}{collected.gaps && collected.gaps.length ? <span className="warn"> · {collected.gaps.length} gap(s)</span> : null}
        </div>
      )}
      {(intents.length > 0 || rejectedByContract.length > 0) && (
        <div className="section">
          <div className="t-cap">Proposed</div>
          {intents.map((e) => {
            const p = e.payload as { type: string; asset: string; target: { kind: string; value?: string }; valid_until: string };
            return <div key={e.id} className="t-sec">• {p.type} {p.asset}{p.target.value ? ` → ${p.target.kind === 'WEIGHT_OF_ALLOCATION' ? `${(Number(p.target.value) * 100).toFixed(0)}% of allocation` : `${(Number(p.target.value) * 100).toFixed(0)}% of position`}` : ''} · valid until {absTime(p.valid_until)}</div>;
          })}
          {rejectedByContract.map((e) => <div key={e.id} className="t-sec muted">• Dropped {String(e.payload['type'])} {String(e.payload['asset'])}: {String(e.payload['reason'])}</div>)}
        </div>
      )}
      {(orders.length > 0 || fills.length > 0 || lastStatus) && (
        <div className="section">
          <div className="t-cap">Executed</div>
          {orders.map((e) => { const p = e.payload as { side: string; qty: string; limit_price: string | null; mode: string }; return <div key={e.id} className="t-sec">• {p.side} {p.qty} @ {p.limit_price ?? 'market'} ({p.mode})</div>; })}
          {fills.map((e) => { const p = e.payload as { qty: string; price: string; currency: string; fee: { amount: string; currency: string } }; return <div key={e.id} className="t-sec">• Fill {p.qty} @ {p.price} {p.currency} · fee {p.fee.amount} {p.fee.currency}</div>; })}
          {lastStatus && <div className="t-sec muted">• {ORDER_TERMINAL.has(lastStatus.to) ? 'Final' : 'Now'}: {lastStatus.to.toLowerCase().replace(/_/g, ' ')}{lastStatus.reason ? ` — ${lastStatus.reason}` : ''}</div>}
        </div>
      )}
      {ledger.length > 0 && (
        <div className="section">
          <div className="t-cap">Effect</div>
          {ledger.map((e) => { const p = e.payload as { amount?: { amount: string }; realized?: { amount: string }; fx_pnl?: { amount: string } }; const label = e.type.replace('ledger.', ''); const amt = p.amount?.amount ?? p.realized?.amount; return <div key={e.id} className="t-sec num">• {label} {amt ? `${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${baseCurrency}` : ''}{p.fx_pnl ? ` (FX ${Number(p.fx_pnl.amount).toFixed(2)})` : ''}</div>; })}
        </div>
      )}
      {(scheduled.length > 0 || rescheduled.length > 0 || cancelled.length > 0 || triggers.length > 0) && (
        <div className="section">
          <div className="t-cap">Plan</div>
          {scheduled.map((e) => <div key={e.id} className="t-sec">• Checkpoint {absTime(String(e.payload['due_at']))}: {String(e.payload['purpose'])}</div>)}
          {rescheduled.map((e) => <div key={e.id} className="t-sec">• Moved → {absTime(String(e.payload['due_at']))} · {String(e.payload['reason'])}</div>)}
          {cancelled.map((e) => <div key={e.id} className="t-sec muted">• Cancelled: {String(e.payload['reason'])}</div>)}
          {triggers.map((e) => { const s = e.payload['spec'] as { asset?: string; threshold_pct?: number; keywords?: string[] }; return <div key={e.id} className="t-sec">• Trigger {String(e.payload['kind'])}{s.asset ? ` ${s.asset}` : ''}{s.threshold_pct ? ` ±${(s.threshold_pct * 100).toFixed(0)}%` : ''}{s.keywords?.length ? ` [${s.keywords.join(', ')}]` : ''}</div>; })}
        </div>
      )}
      {open && (
        <div className="section">
          {chapter.reportId && <Link to="/n/$slug/report/$id" params={{ slug, id: chapter.reportId }} className="t-sec">Open the full report →</Link>}
          {ai.map((e) => <div key={e.id} className="t-sec faint">{String(e.payload['prompt_id'])} · {String(e.payload['model'])} · ${Number(e.payload['cost_usd']).toFixed(3)}</div>)}
          <div className="mono faint" style={{ marginTop: 6 }}>{evs.map((e) => <div key={e.id}>{e.seq} · {e.type} · {e.actor}</div>)}</div>
        </div>
      )}
    </div>
  );
}
